import { Box, Typography, Paper, Avatar, Chip, Stack } from '@mui/material';
import { Cancel as ErrorIcon } from '@mui/icons-material';

interface StatusChip {
  label: string;
  color: 'success' | 'warning';
}

export interface StatusPageProps {
  variant: 'error' | 'configuration-required';
  icon?: React.ComponentType<{ sx?: any }>;
  title: string;
  message: string;
  chips?: StatusChip[];
  footnote?: string;
}

const variantStyles = {
  error: {
    background: 'linear-gradient(145deg, #fef2f2 0%, #fee2e2 100%)',
    border: '1px solid #fca5a5',
    topBorder: 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)',
    avatarBg: '#fecaca',
    avatarColor: '#dc2626',
    titleColor: '#dc2626',
    messageColor: '#7f1d1d',
    maxWidth: 500,
    minHeight: undefined as string | undefined,
    py: 12,
  },
  'configuration-required': {
    background: 'linear-gradient(145deg, #fffbeb 0%, #fef3c7 100%)',
    border: '2px solid #f59e0b',
    topBorder: undefined as string | undefined,
    avatarBg: 'rgba(245, 158, 11, 0.1)',
    avatarColor: '#f59e0b',
    titleColor: '#92400e',
    messageColor: '#78350f',
    maxWidth: 700,
    minHeight: '70vh',
    py: undefined as number | undefined,
  },
};

const StatusPage: React.FC<StatusPageProps> = ({ variant, icon: CustomIcon, title, message, chips, footnote }) => {
  const styles = variantStyles[variant];
  const Icon = CustomIcon || ErrorIcon;

  const isConfig = variant === 'configuration-required';

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems={isConfig ? 'center' : undefined}
      minHeight={styles.minHeight}
      sx={{ textAlign: 'center', py: styles.py }}
    >
      <Paper
        elevation={0}
        sx={{
          p: isConfig ? 6 : 4,
          maxWidth: styles.maxWidth,
          background: styles.background,
          border: styles.border,
          borderRadius: isConfig ? 4 : 3,
          position: 'relative',
          overflow: 'hidden',
          ...(isConfig && { boxShadow: '0 10px 40px rgba(245, 158, 11, 0.1)' }),
          ...(styles.topBorder && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: '4px',
              background: styles.topBorder,
            },
          }),
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Avatar sx={{
            width: isConfig ? 80 : 56,
            height: isConfig ? 80 : 56,
            backgroundColor: styles.avatarBg,
            color: styles.avatarColor,
            ...(isConfig && { border: `3px solid ${styles.avatarColor}30` }),
            mx: 'auto',
            mb: isConfig ? 3 : 2,
          }}>
            <Icon sx={{ fontSize: isConfig ? 40 : 28 }} />
          </Avatar>

          <Typography
            variant={isConfig ? 'h4' : 'h6'}
            sx={{ fontWeight: isConfig ? 800 : 700, color: styles.titleColor, mb: 2, ...(isConfig && { letterSpacing: '-0.02em' }) }}
          >
            {title}
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: styles.messageColor, lineHeight: 1.6, ...(isConfig && { mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }) }}
          >
            {message}
          </Typography>

          {chips && chips.length > 0 && (
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4, mt: isConfig ? 0 : 2 }}>
              {chips.map((chip) => (
                <Chip
                  key={chip.label}
                  label={chip.label}
                  color={chip.color}
                  variant={chip.color === 'success' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, fontSize: '0.9rem', px: 2, py: 1, height: 'auto' }}
                />
              ))}
            </Stack>
          )}

          {footnote && (
            <Typography variant="body2" sx={{ color: styles.messageColor, fontStyle: 'italic', opacity: 0.8 }}>
              {footnote}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default StatusPage;
